import { useState, useEffect } from "react";
import { supabase } from "@/integrations/supabase/client";
import { useAuth } from "@/contexts/AuthContext";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Switch } from "@/components/ui/switch";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { toast } from "sonner";

const DEPARTMENTS = [
  "Academic", "Admission", "Administration", "Examination",
  "Accounting", "Student Services", "Discipline", "ICT", "General",
];

const MEETING_TYPES = [
  { value: "regular", label: "Regular Meeting" },
  { value: "emergency", label: "Emergency Meeting" },
  { value: "board", label: "Board Meeting" },
  { value: "committee", label: "Committee Meeting" },
  { value: "agm", label: "Annual General Meeting (AGM)" },
  { value: "departmental", label: "Departmental Meeting" },
];

interface Profile {
  user_id: string;
  full_name: string | null;
  salutation: string | null;
}

interface MeetingSetupDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  onCreated: () => void;
}

export default function MeetingSetupDialog({ open, onOpenChange, onCreated }: MeetingSetupDialogProps) {
  const { user } = useAuth();
  const [title, setTitle] = useState("");
  const [department, setDepartment] = useState("");
  const [meetingType, setMeetingType] = useState("regular");
  const [scheduledAt, setScheduledAt] = useState("");
  const [location, setLocation] = useState("");
  const [chairUserId, setChairUserId] = useState("");
  const [secretaryUserId, setSecretaryUserId] = useState("");
  const [recordingEnabled, setRecordingEnabled] = useState(true);
  const [transcriptionEnabled, setTranscriptionEnabled] = useState(true);
  const [creating, setCreating] = useState(false);
  const [profiles, setProfiles] = useState<Profile[]>([]);

  useEffect(() => {
    if (open) loadProfiles();
  }, [open]);

  const loadProfiles = async () => {
    const { data } = await supabase
      .from("profiles")
      .select("user_id, full_name, salutation")
      .eq("status", "active")
      .order("full_name");
    setProfiles(data || []);
  };

  const profileLabel = (p: Profile) => [p.salutation, p.full_name].filter(Boolean).join(" ") || "Unknown";

  const createMeeting = async () => {
    if (!title.trim() || !user) return;
    setCreating(true);
    const roomName = `stdavid-${crypto.randomUUID().slice(0, 8)}`;
    const { error } = await supabase.from("meetings").insert({
      title: title.trim(),
      room_name: roomName,
      department: department || null,
      created_by: user.id,
      scheduled_at: scheduledAt || null,
      status: "scheduled",
      meeting_type: meetingType,
      chair_user_id: chairUserId || null,
      secretary_user_id: secretaryUserId || null,
      recording_enabled: recordingEnabled,
      transcription_enabled: transcriptionEnabled,
      location: location || null,
    } as any);
    if (error) {
      toast.error(error.message);
    } else {
      toast.success("Meeting created");
      resetForm();
      onOpenChange(false);
      onCreated();
    }
    setCreating(false);
  };

  const resetForm = () => {
    setTitle(""); setDepartment(""); setMeetingType("regular");
    setScheduledAt(""); setLocation(""); setChairUserId("");
    setSecretaryUserId(""); setRecordingEnabled(true); setTranscriptionEnabled(true);
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-lg max-h-[90vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle>Create Meeting</DialogTitle>
        </DialogHeader>
        <div className="space-y-4">
          <div>
            <Label>Meeting Title *</Label>
            <Input value={title} onChange={(e) => setTitle(e.target.value)} placeholder="e.g. Weekly Staff Meeting" />
          </div>

          <div className="grid grid-cols-2 gap-3">
            <div>
              <Label>Meeting Type</Label>
              <Select value={meetingType} onValueChange={setMeetingType}>
                <SelectTrigger><SelectValue /></SelectTrigger>
                <SelectContent>
                  {MEETING_TYPES.map(t => <SelectItem key={t.value} value={t.value}>{t.label}</SelectItem>)}
                </SelectContent>
              </Select>
            </div>
            <div>
              <Label>Department</Label>
              <Select value={department} onValueChange={setDepartment}>
                <SelectTrigger><SelectValue placeholder="Select" /></SelectTrigger>
                <SelectContent>
                  {DEPARTMENTS.map(d => <SelectItem key={d} value={d}>{d}</SelectItem>)}
                </SelectContent>
              </Select>
            </div>
          </div>

          <div className="grid grid-cols-2 gap-3">
            <div>
              <Label>Chairperson</Label>
              <Select value={chairUserId} onValueChange={setChairUserId}>
                <SelectTrigger><SelectValue placeholder="Select chair" /></SelectTrigger>
                <SelectContent>
                  {profiles.map(p => (
                    <SelectItem key={p.user_id} value={p.user_id}>{profileLabel(p)}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            <div>
              <Label>Secretary</Label>
              <Select value={secretaryUserId} onValueChange={setSecretaryUserId}>
                <SelectTrigger><SelectValue placeholder="Select secretary" /></SelectTrigger>
                <SelectContent>
                  {profiles.map(p => (
                    <SelectItem key={p.user_id} value={p.user_id}>{profileLabel(p)}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
          </div>

          <div className="grid grid-cols-2 gap-3">
            <div>
              <Label>Scheduled Date & Time</Label>
              <Input type="datetime-local" value={scheduledAt} onChange={(e) => setScheduledAt(e.target.value)} />
            </div>
            <div>
              <Label>Location</Label>
              <Input value={location} onChange={(e) => setLocation(e.target.value)} placeholder="e.g. Board Room" />
            </div>
          </div>

          <div className="flex items-center gap-6">
            <div className="flex items-center gap-2">
              <Switch checked={recordingEnabled} onCheckedChange={setRecordingEnabled} />
              <Label className="text-sm">Recording</Label>
            </div>
            <div className="flex items-center gap-2">
              <Switch checked={transcriptionEnabled} onCheckedChange={setTranscriptionEnabled} />
              <Label className="text-sm">Transcription</Label>
            </div>
          </div>
        </div>
        <DialogFooter>
          <Button onClick={createMeeting} disabled={creating || !title.trim()}>
            {creating ? "Creating..." : "Create Meeting"}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
